import { $, $$, on } from '../core/dom.js';
import { MODE, API_BASE, SUBMISSIONS_OPEN } from '../core/config.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const TOTAL_STEPS = 3;
const STEP_OF_FIELD = { name: 1, email: 3, consentArchive: 3 };

let els = {};
let step = 1;

export function initRequestForm() {
  els = {
    form:           $('#gen-request-form'),
    steps:          $$('.wizard-steps__item'),
    panels:         $$('.wizard-panel'),
    name:           $('#gen-name'),
    email:          $('#gen-email'),
    consentArchive: $('#gen-c-archive'),
    honeypot:       $('#gen-website'),
    submit:         $('#gen-submit'),
    error:          $('#gen-request-error'),
    stateForm:      $('#gen-state-form'),
    stateDone:      $('#gen-state-success'),
    sentTo:         $('#gen-sent-to'),
    demoNote:       $('#gen-demo-note'),
    again:          $('#gen-again')
  };
  if (!els.form) return;

  $$('[data-wizard-next]', els.form).forEach(btn => on(btn, 'click', next));
  $$('[data-wizard-prev]', els.form).forEach(btn => on(btn, 'click', prev));
  on(els.form, 'submit', submit);
  on(els.again, 'click', resetRequestForm);

  // Fjern fejlmarkering så snart brugeren retter feltet
  [els.name, els.email].forEach(input => on(input, 'input', () => clearFieldError(input)));
  on(els.consentArchive, 'change', () => clearFieldError(els.consentArchive));

  goToStep(1);
}

/* ── Trin ───────────────────────────────────── */

function goToStep(n) {
  step = n;
  els.panels.forEach(panel => {
    const active = Number(panel.dataset.wizardPanel) === n;
    panel.classList.toggle('is-active', active);
    panel.hidden = !active;
  });
  els.steps.forEach(item => item.classList.toggle('is-active', Number(item.dataset.wizardStep) <= n));
  els.form.closest('.request-card__state')?.scrollIntoView?.({ block: 'nearest' });
}

function next() {
  if (step === 1 && !validateStep1()) return;
  goToStep(Math.min(step + 1, TOTAL_STEPS));
}

function prev() {
  goToStep(Math.max(step - 1, 1));
}

function validateStep1() {
  clearFieldError(els.name);
  if (els.name.value.trim().length < 2) {
    showFieldErrors({ name: 'Skriv fortællerens fulde navn.' });
    return false;
  }
  return true;
}

/* ── Validering ─────────────────────────────── */

function validate() {
  const errors = {};
  if (els.name.value.trim().length < 2)        errors.name           = 'Skriv fortællerens fulde navn.';
  if (!EMAIL_RE.test(els.email.value.trim()))  errors.email          = 'Skriv en gyldig e-mailadresse.';
  if (!els.consentArchive.checked)             errors.consentArchive = 'Du skal give samtykke til arkivering for at fortsætte.';
  return errors;
}

function fieldFor(key) {
  return { name: els.name, email: els.email, consentArchive: els.consentArchive }[key];
}

function showFieldErrors(errors) {
  const firstKey = Object.keys(errors)[0];
  const targetStep = STEP_OF_FIELD[firstKey];
  if (targetStep && targetStep !== step) goToStep(targetStep);

  Object.entries(errors).forEach(([key, msg]) => {
    const input = fieldFor(key);
    if (!input) return;
    input.classList.add('is-invalid');
    input.setAttribute('aria-invalid', 'true');
    const slot = $(`[data-error-for="${input.id}"]`);
    if (slot) slot.textContent = msg;
  });
  fieldFor(firstKey)?.focus();
}

function clearFieldError(input) {
  input.classList.remove('is-invalid');
  input.removeAttribute('aria-invalid');
  const slot = $(`[data-error-for="${input.id}"]`);
  if (slot) slot.textContent = '';
}

function clearAllErrors() {
  [els.name, els.email, els.consentArchive].forEach(clearFieldError);
  els.error.classList.remove('is-visible');
}

/* ── Indsamling af data ─────────────────────── */

const val = id => ($(`#${id}`)?.value ?? '').trim();

function collectPayload() {
  return {
    // Del 1 — fortællerens oplysninger
    name:              els.name.value.trim(),
    alias:             val('gen-alias'),
    gender:            val('gen-gender'),
    birthdate:         val('gen-birthdate'),
    birthplace:        val('gen-birthplace'),
    origin:            val('gen-origin'),
    arrivalYear:       val('gen-arrival'),
    residence:         val('gen-residence'),
    // Del 2 — om optagelsen
    interviewDate:     val('gen-idate'),
    interviewPlace:    val('gen-iplace'),
    interviewer:       val('gen-interviewer'),
    relation:          val('gen-relation'),
    language:          val('gen-language'),
    interviewerEmail:  val('gen-iemail'),
    interviewerPhone:  val('gen-iphone'),
    // Del 3 — samtykke + kontakt
    consentArchive:    els.consentArchive.checked,
    consentPartners:   $('#gen-c-partners')?.checked ?? false,
    consentPublic:     $('#gen-c-public')?.checked ?? false,
    email:             els.email.value.trim(),
    website:           els.honeypot.value
  };
}

/* ── Afsendelse ─────────────────────────────── */

async function submit(e) {
  e.preventDefault();
  if (!SUBMISSIONS_OPEN) return;
  clearAllErrors();

  const errors = validate();
  if (Object.keys(errors).length) return showFieldErrors(errors);

  const payload = collectPayload();

  setLoading(true);
  try {
    if (MODE === 'demo') await fakeRequest();
    else await sendRequest(payload);
    showSuccess(payload.email);
  } catch (err) {
    if (err.fields) showFieldErrors(err.fields);
    els.error.textContent = err.message;
    els.error.classList.add('is-visible');
  } finally {
    setLoading(false);
  }
}

async function sendRequest(payload) {
  let res;
  try {
    res = await fetch(`${API_BASE}/upload-request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  } catch {
    throw new Error('Kunne ikke kontakte serveren. Tjek din internetforbindelse.');
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || 'Noget gik galt. Prøv igen.');
    err.fields = data.fields;
    throw err;
  }
}

const fakeRequest = () => new Promise(r => setTimeout(r, 900));

/* ── Tilstande ──────────────────────────────── */

function setLoading(on) {
  els.submit.disabled = on;
  els.submit.classList.toggle('is-loading', on);
}

function showSuccess(email) {
  els.sentTo.textContent = email;
  els.demoNote.hidden = MODE !== 'demo';
  els.stateForm.classList.remove('is-active');
  els.stateDone.classList.add('is-active');
}

export function resetRequestForm() {
  els.form?.reset();
  clearAllErrors();
  goToStep(1);
  els.stateDone?.classList.remove('is-active');
  els.stateForm?.classList.add('is-active');
}
